import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, FileUp } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { STATUSES } from "../lib/constants";
import { StatusBadge, AwaitingBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const filters = {
    q: params.get("q") || "",
    status: params.get("status") || "",
    category_id: params.get("category_id") || "",
    city: params.get("city") || "",
    page: parseInt(params.get("page") || "1"),
  };

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
    qs.set("limit", "25");
    api.get(`/listings?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); setSelected([]); }, [params]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    if (k !== "page") p.delete("page");
    setParams(p);
  };

  const topCats = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const pages = Math.max(1, Math.ceil(data.total / 25));

  const toggleAll = (checked) => setSelected(checked ? data.items.map((i) => i.id) : []);
  const toggleOne = (id, checked) => setSelected((s) => (checked ? [...s, id] : s.filter((x) => x !== id)));

  const applyBulkStatus = async () => {
    if (!bulkStatus || selected.length === 0) return;
    try {
      const r = await api.post("/listings/bulk-status", { ids: selected, status: bulkStatus });
      toast.success(`${r.data.updated} listing(s) updated to ${bulkStatus}`);
      setBulkStatus("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} listing(s)? This cannot be undone.`)) return;
    try {
      const r = await api.post("/listings/bulk-delete", { ids: selected });
      toast.success(`${r.data.deleted} listing(s) deleted`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="listings-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1B2A4A]">Listings</h1>
          <p className="text-sm text-[#475569] mt-1">{data.total} business{data.total === 1 ? "" : "es"} in the directory</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="go-to-import-button" onClick={() => navigate("/admin/import")} className="border-[#E2E8F0]">
            <FileUp size={15} className="mr-2" /> CSV Import
          </Button>
          <Button data-testid="add-listing-button" onClick={() => navigate("/admin/listings/new")} className="bg-[#F5961B] hover:bg-[#dd8512] text-white">
            <Plus size={15} className="mr-2" /> Add Business
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 bg-white border border-[#E2E8F0] rounded-xl p-4">
        <Input
          data-testid="listings-search-input"
          placeholder="Search by name..."
          value={filters.q}
          onChange={(e) => setFilter("q", e.target.value)}
          className="w-56"
        />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="listings-status-filter" className="w-44"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category_id || "all"} onValueChange={(v) => setFilter("category_id", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="listings-category-filter" className="w-56"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All categories</SelectItem>
            {topCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.city || "all"} onValueChange={(v) => setFilter("city", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="listings-city-filter" className="w-52"><SelectValue placeholder="All cities" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All cities</SelectItem>
            {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selected.length > 0 && (
        <div data-testid="bulk-actions-bar" className="flex items-center gap-3 bg-[#1B2A4A] text-white rounded-xl px-4 py-3">
          <span className="text-sm font-medium">{selected.length} selected</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger data-testid="bulk-status-select" className="w-40 h-8 bg-white/10 border-white/20 text-white"><SelectValue placeholder="Set status" /></SelectTrigger>
            <SelectContent className="bg-white">
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" data-testid="bulk-apply-button" onClick={applyBulkStatus} disabled={!bulkStatus} className="bg-[#2E7BD6] hover:bg-[#2568b8] text-white h-8">Apply</Button>
          <Button size="sm" variant="outline" data-testid="bulk-delete-button" onClick={bulkDelete} className="h-8 border-red-300 text-red-100 hover:bg-red-500/20 bg-transparent">
            <Trash2 size={13} className="mr-1.5" /> Delete
          </Button>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="listings-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left">
              <th className="px-4 py-3 w-10"><Checkbox data-testid="select-all-checkbox" checked={selected.length === data.items.length && data.items.length > 0} onCheckedChange={toggleAll} /></th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Business</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Category</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">City</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Status</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Contact</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Entered By</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]">Loading...</td></tr>}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#94A3B8]" data-testid="listings-empty">No listings found. Add your first business.</td></tr>
            )}
            {!loading && data.items.map((b) => (
              <tr key={b.id} data-testid={`listing-row-${b.id}`} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => navigate(`/admin/listings/${b.id}`)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox data-testid={`select-listing-${b.id}`} checked={selected.includes(b.id)} onCheckedChange={(c) => toggleOne(b.id, c)} />
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0F172A]">{b.name}</div>
                  {b.pending_docs > 0 && <span className="text-xs text-[#F5961B] font-medium">{b.pending_docs} doc(s) pending review</span>}
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  {(b.category_names || []).length > 0 ? b.category_names.join(", ") : <span className="text-[#F5961B]">—</span>}
                  {(b.subcategory_names || []).length > 0 && <span className="text-[#94A3B8]"> / {b.subcategory_names.join(", ")}</span>}
                </td>
                <td className="px-4 py-3 text-[#475569]">{b.city || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <StatusBadge status={b.status} testid={`listing-status-${b.id}`} />
                    {b.awaiting_category && <AwaitingBadge />}
                  </div>
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  <div>{(b.phones || []).join(" · ") || "—"}</div>
                  <div className="text-xs text-[#94A3B8]">{b.email}</div>
                </td>
                <td className="px-4 py-3 text-[#475569]">{b.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]">
            <span className="text-sm text-[#475569]">Page {filters.page} of {pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="prev-page-button" disabled={filters.page <= 1} onClick={() => setFilter("page", String(filters.page - 1))}>Previous</Button>
              <Button size="sm" variant="outline" data-testid="next-page-button" disabled={filters.page >= pages} onClick={() => setFilter("page", String(filters.page + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
